import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import fullColourIconUrl from '@resources/img/pages/products/printed-patches/icon-4.svg?url';
import pvcIconUrl from '@resources/img/pages/products/printed-patches/icon-1.svg?url';
import canadaIconUrl from '@resources/img/pages/products/printed-patches/icon-5.svg?url';
import capIconUrl from '@resources/img/pages/products/snapback-with-printed-patch/icon-4.svg?url';
import adjustableIconUrl from '@resources/img/pages/products/snapback-with-printed-patch/icon-3.svg?url';
import measurementsUrl from '@resources/img/pages/products/dad-cap-with-printed-patch/measurements.svg?url';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';
import SizeRangeDisplay from '../../../Common/Measurements/SizeRangeDisplay';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionPricing from '../../_Sections/SectionPricing';
import SectionSizes from '../../_Sections/SectionSizes';
import PrintedPatchMeasurementDiagram from '../Patches/PrintedPatchMeasurementDiagram';

const PRODUCT_ID = 140;
const VIEW_PRODUCT_ID = 172;

const bannerImages = {
  en_CA: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/banner-1-en_CA.png', import.meta.url).href,
  fr_CA: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/banner-1-fr_CA.png', import.meta.url).href,
};

const galleryImages = [
  new URL('@resources/img/pages/products/dad-cap-with-printed-patch/gallery_0.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/dad-cap-with-printed-patch/gallery_1.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/dad-cap-with-printed-patch/gallery_2.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/dad-cap-with-printed-patch/gallery_3.jpg', import.meta.url).href,
  new URL('@resources/img/pages/products/dad-cap-with-printed-patch/gallery_4.jpg', import.meta.url).href,
];

const colors = [
  { name: 'White', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/white.webp', import.meta.url).href },
  { name: 'Black', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/black.webp', import.meta.url).href },
  { name: 'Dark Grey', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/dark_grey.webp', import.meta.url).href },
  { name: 'Cranberry', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/cranberry.webp', import.meta.url).href },
  { name: 'Khaki', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/khaki.webp', import.meta.url).href },
  { name: 'Pink', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/pink.webp', import.meta.url).href },
  { name: 'Stone', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/stone.webp', import.meta.url).href },
  { name: 'Navy', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/navy.webp', import.meta.url).href },
  { name: 'Light Blue', image: new URL('@resources/img/pages/products/dad-cap-with-printed-patch/colors/light_blue.webp', import.meta.url).href },
];

export default function DadCapWithPrintedPatchPage() {
  const [t] = useTranslation('page/products/dad-cap-with-printed-patch');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const bannerImage = locale === 'fr_CA' ? bannerImages.fr_CA : bannerImages.en_CA;

  if (!productData) return null;

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom Dad Cap with Printed Patch | Products')}
      description={t('Create a custom vegan leather patch Dad Hat with your artwork, logo, or design. ✔ No minimums ✔ Made to order in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#abebb9"
      bgStyle={{ backgroundPosition: 'bottom right', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }}
      tag={t('Placement print')}
      title={t('Dad Cap with Printed Patch')}
      text={t("Create your own spin on a classic with our custom Dad Cap with printed patch! Featuring a relaxed, low-profile fit and a completely custom vegan leather patch, this hat turns your artwork into a wearable statement. Choose from multiple hat colours, patch shapes, and sizes to create a look that's all your own!")}
      createUrl={productData.get('url_slug')}
      img={bannerImage}
      alt={t('Picture of Dad Cap with Printed Patch')}
      details={[
        { img: fullColourIconUrl, alt: t('Full colour printing icon'), text: t('Custom full-colour, vibrant printed patch') },
        { img: pvcIconUrl, alt: t('PVC icon'), text: t('Made from durable, waterproof PVC') },
        { img: capIconUrl, alt: t('Dad cap icon'), text: t('Classic Dad Hat from YP Classics made from 100% cotton chino twill') },
        { img: adjustableIconUrl, alt: t('Adjustable dad cap icon'), text: t('Comfortable, unstructured, adjustable fit') },
        { img: canadaIconUrl, alt: t('Canada icon'), text: t('Digitally printed on demand in Montreal, Canada') },
      ]}
    />
    <SectionGallery images={galleryImages} />
    <SectionPricing productId={PRODUCT_ID} options={['circle_2']} />
    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Dad Cap with Printed Patch Sizing')}
      text={t('Dad hats are adjustable with an unstructured, six-panel, low-profile fit. Patches are available in 4 shapes and 2 sizes per shape. Please allow for some variance in the placement of your design within an order. Patches are handled individually by staff, so there may be some differences in image placement.')}
    >
      <Stack spacing={8} sx={{ mt: 6 }}>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h4" component="h2" color="common.white">{t('Dad Cap Sizing')}</Typography>
          <Box
            component="svg"
            viewBox="0 0 1000 352.241"
            role="img"
            aria-label={t('Dad cap size chart')}
            sx={{
              display: 'block', height: 'auto', maxWidth: 800, width: '100%',
              '& .dad-cap-measurement-label': { fontSize: { xs: 32, sm: 26, md: 21 } },
            }}
          >
            <image aria-hidden href={measurementsUrl} height="352.241" width="883.06" x="58.47" />
            <text className="dad-cap-measurement-label" fill="#fff" fontFamily="Poppins, sans-serif"
              fontWeight="500" textAnchor="end" x="148" y="137">
              <SizeDisplay sizes={[3.125]} sourceUnit="in" displayType="physical" />
            </text>
            <text className="dad-cap-measurement-label" fill="#fff" fontFamily="Poppins, sans-serif"
              fontWeight="500" textAnchor="middle" x="790" y="310">
              <SizeRangeDisplay minSizes={[6.625]} maxSizes={[7.375]} sourceUnit="in" displayType="physical" separator=" – " />
            </text>
          </Box>
        </Stack>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h4" component="h2" color="common.white">{t('Printed Patch Shapes and Sizes')}</Typography>
          <PrintedPatchMeasurementDiagram ariaLabel={t('Printed Patch shapes and sizes')} />
        </Stack>
      </Stack>
    </SectionSizes>
    <Box component="section" sx={{ backgroundColor: '#333', py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Stack spacing={6} alignItems="center">
          <Typography variant="h1" component="h2" color="common.white" textAlign="center">{t('Hat colours')}</Typography>
          <Box sx={{ display: 'grid', gap: { xs: 3, md: 4 }, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, width: '100%' }}>
            {colors.map(color => <Stack key={color.name} spacing={2} alignItems="center">
              <img src={color.image} alt={t(color.name)} loading="lazy" style={{ height: 'auto', maxWidth: 150, width: '100%' }} />
              <Typography variant="h5" component="h3" color="common.white" textAlign="center">{t(color.name)}</Typography>
            </Stack>)}
          </Box>
        </Stack>
      </Container>
    </Box>
    <SectionBranding
      title={t('Brand your Dad Cap with Printed Patch')}
      text={t('Branding enhances your products. Add custom artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />
    <SectionPairings
      text={t('Pair your custom dad cap with these great products to create your own unique style.')}
      productSlugs={['drawstring-bag', 'premium-t-shirt', 'stainless-steel-water-bottle']}
    />
  </div>;
}
